import type {BridgeComponent, BridgeComponentConstructor} from '../types';

/**
 * Creates a Vue 3 driver for a bridge component.
 * Returns a Vue component options object that:
 * - On mounted: creates bridge instance, renders into template div
 * - On $attrs change: calls updateProps
 * - On unmounted: calls destroy
 * - Exposes bridge instance via template ref
 */
export function createVue3Driver<P extends Record<string, any>>(
  ComponentClass: BridgeComponentConstructor<P>
): () => any {
  return () => ({
    template: '<div></div>',
    inheritAttrs: false,

    mounted(this: VueInstance<P>) {
      const el = this.$el;
      this.__bridgeInstance = new ComponentClass({...this.$attrs} as P);
      this.__bridgeInstance.render(el).catch(console.error);
    },

    watch: {
      $attrs: {
        handler(this: VueInstance<P>) {
          if (this.__bridgeInstance) {
            this.__bridgeInstance.updateProps({...this.$attrs} as Partial<P>).catch(() => {});
          }
        },
        deep: true
      }
    },

    unmounted(this: VueInstance<P>) {
      if (this.__bridgeInstance) {
        this.__bridgeInstance.destroy();
        this.__bridgeInstance = null;
      }
    }
  });
}

type VueInstance<P> = {
  $el: HTMLElement;
  $attrs: Record<string, any>;
  __bridgeInstance: BridgeComponent<P> | null;
};
